import React, { useState } from "react";
import {
  useGame,
  usePlayer,
  usePlayers,
} from "@empirica/core/player/classic/react";
import { Button } from "../components/Button";
import { TestContinueWithoutInteraction } from "../components/TestContinueWithoutInteraction";
import {
  DemandAgainstPotChart,
  DonutChart,
} from "./NegotiationDonutCharts";
import { dollars } from "../formatMoney";
import {
  LossFrameDemandBarCard,
  LossFrameMoneyKeptBarCard,
} from "../stage2/Stage2ResultStage";
import {
  getCounterpartCopy,
  getNegotiationTermCopy,
  isLossFrameDesign,
} from "../payoffFrame";

function AmountCard({ label, value, colorClassName = "text-gray-900" }) {
  return (
    <div className="rounded-md border border-gray-200 p-4 bg-white">
      <div className="text-xl text-gray-600">{label}</div>
      <div className={`text-3xl font-semibold mt-1 ${colorClassName}`}>
        {dollars(value)}
      </div>
    </div>
  );
}

function cents(value) {
  return Math.round((Number(value) || 0) * 100);
}

function finiteDollarOrNull(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

export function NegotiationOutcomeStage({ next }) {
  const game = useGame();
  const player = usePlayer();
  const players = usePlayers();
  const design = game.get("design") || {};
  const lossFrame = isLossFrameDesign(design);
  const counterpart = getCounterpartCopy(design);
  const termCopy = getNegotiationTermCopy(design);

  const stage1 = game.get("stage1") || {};
  const stage2 = game.get("stage2") || {};
  const partner = players.find((p) => p.id !== player.id);
  const partnerId = partner?.id;

  const myFirstAsk = Number(stage2.initialDollarAsksByPlayerId?.[player.id] ?? 0);
  const partnerFirstAsk = Number(stage2.initialDollarAsksByPlayerId?.[partnerId] ?? 0);
  const myFinalAsk = finiteDollarOrNull(stage2.finalDollarAsksByPlayerId?.[player.id]);
  const partnerFinalAsk = finiteDollarOrNull(stage2.finalDollarAsksByPlayerId?.[partnerId]);
  const hasFinalDemandValues = myFinalAsk !== null && partnerFinalAsk !== null;
  const [selectedDemandRound, setSelectedDemandRound] = useState(() =>
    hasFinalDemandValues ? "final" : "first"
  );
  const effectiveDemandRound = hasFinalDemandValues ? selectedDemandRound : "first";
  const myAsk = hasFinalDemandValues ? myFinalAsk : myFirstAsk;
  const partnerAsk = hasFinalDemandValues ? partnerFinalAsk : partnerFirstAsk;
  const showingFirstDemands = effectiveDemandRound === "first";
  const displayedMyAsk = showingFirstDemands ? myFirstAsk : myAsk;
  const displayedPartnerAsk = showingFirstDemands ? partnerFirstAsk : partnerAsk;
  const displayedDemandLabel = showingFirstDemands ? "Initial" : "Final";
  const payoffDemandLabel = hasFinalDemandValues
    ? `final ${termCopy.term}`
    : `initial ${termCopy.term}`;
  const yourSegmentLabel = `Your ${termCopy.term}`;
  const partnerSegmentLabel = `${counterpart.capitalizedTerm} ${termCopy.term}`;
  const teamWinningsBeforeAdjustment = Number(stage2.teamWinningsBeforeAdjustment ?? 0);
  const teamWinnings = Number(stage2.teamWinnings ?? 0);
  const myPayoff = Number(player.get("stage2Earnings") ?? 0);
  const storedPartnerPayoff = Number(partner?.get("stage2Earnings"));

  const triggeredRandomSplit = Boolean(stage2.failedAgreementPenaltyApplied);
  const displayedTotalAsked = displayedMyAsk + displayedPartnerAsk;
  const displayedRemainingWinnings = Math.max(
    0,
    teamWinningsBeforeAdjustment - displayedTotalAsked
  );
  const displayedOverDemand = Math.max(
    0,
    displayedTotalAsked - teamWinningsBeforeAdjustment
  );
  const displayedTotalAskedPercent =
    teamWinningsBeforeAdjustment > 0
      ? Math.round((displayedTotalAsked / teamWinningsBeforeAdjustment) * 100)
      : 0;
  const displayedDemandsFit =
    displayedTotalAsked <= teamWinningsBeforeAdjustment;
  const displayedRemainingWinningsCents = cents(displayedRemainingWinnings);
  const displayedRemainingWinningsRounded = displayedRemainingWinningsCents / 100;
  const hasDisplayedSurplus = displayedRemainingWinningsCents > 0;
  const finalTotalAsked = myAsk + partnerAsk;
  const finalRemainingWinnings = Math.max(
    0,
    teamWinningsBeforeAdjustment - finalTotalAsked
  );
  const finalRemainingWinningsCents = cents(finalRemainingWinnings);
  const hasFinalSurplus = finalRemainingWinningsCents > 0;
  const myFinalPayoff = myPayoff;
  const partnerFinalPayoff = Number.isFinite(storedPartnerPayoff)
    ? storedPartnerPayoff
    : Math.max(0, (triggeredRandomSplit ? teamWinnings : teamWinningsBeforeAdjustment) - myPayoff);
  const myFinalSurplus = Math.max(0, myFinalPayoff - myAsk);
  const partnerFinalSurplus = Math.max(0, partnerFinalPayoff - partnerAsk);
  const myFinalLoss = Math.max(0, teamWinningsBeforeAdjustment - myFinalPayoff);
  const shrinkLoss = triggeredRandomSplit
    ? Math.max(0, teamWinningsBeforeAdjustment - teamWinnings)
    : 0;

  function handleContinue() {
    next();
  }

  return (
    <div className="max-w-4xl mx-auto px-8 py-6 space-y-6">
      <div className="space-y-3">
        <h2 className="text-4xl font-semibold text-gray-900">Negotiation Outcome</h2>
        <div
          className={`rounded-md border p-4 text-2xl font-semibold ${
            triggeredRandomSplit
              ? "border-red-200 bg-red-50 text-red-900"
              : "border-emerald-200 bg-emerald-50 text-emerald-900"
          }`}
        >
          {triggeredRandomSplit ? (
            <>
              {lossFrame ? (
                <>
                  <div className="text-3xl">
                    Your {termCopy.pluralTerm} to keep savings were{" "}
                    <b>larger than the saved amount</b>.
                  </div>
                  <div className="text-3xl">So, the saved money was <b>shrunk</b> and <b>split randomly</b>.</div>
                </>
              ) : (
                <>
                  <div className="text-3xl">
                    Your <b>combined</b> {termCopy.pluralTerm} were{" "}
                    <b>larger than the pot</b>.
                  </div>
                  <div className="text-3xl">So, the pot was <b>shrunk</b> and <b>split randomly</b>.</div>
                </>
              )}
            </>
          ) : (
            <>
              {lossFrame ? (
                <>
                  <div className="text-3xl">
                    Your <b>combined</b> {termCopy.pluralTerm} fit inside the
                    saved amount.
                  </div>
                  <div className="text-3xl">So, you kept the negotiated amount.</div>
                </>
              ) : (
                <>
                  <div className="text-3xl">
                    Your <b>combined</b> {termCopy.pluralTerm} fit inside the pot.
                  </div>
                  <div className="text-3xl">
                    So, you <b>received your {termCopy.pluralTerm}</b>.
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <div
          className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-1"
          role="tablist"
          aria-label={`${termCopy.capitalizedTerm} round`}
        >
          {[
            { id: "first", label: `Initial ${termCopy.pluralTerm}` },
            ...(hasFinalDemandValues
              ? [{ id: "final", label: `Final ${termCopy.pluralTerm}` }]
              : []),
          ].map((tab) => {
            const selected = effectiveDemandRound === tab.id;

            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={selected}
                className={`rounded px-4 py-2 text-2xl font-semibold transition ${
                  selected
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setSelectedDemandRound(tab.id)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <AmountCard label={lossFrame ? "Saved amount" : "Total pot"} value={teamWinningsBeforeAdjustment} />
        <AmountCard
          label={`Your ${termCopy.term}`}
          value={displayedMyAsk}
          colorClassName="text-blue-700"
        />
        <AmountCard
          label={`${counterpart.capitalizedTerm} ${termCopy.term}`}
          value={displayedPartnerAsk}
          colorClassName="text-red-700"
        />
      </div>

      {lossFrame ? (
        <div className="grid lg:grid-cols-2 gap-4">
          <LossFrameDemandBarCard
            title={`Your ${displayedDemandLabel} ${termCopy.capitalizedTerm}`}
            actorLabel="You"
            demand={displayedMyAsk}
            savingsAtStakeDollars={teamWinningsBeforeAdjustment}
            demandLabel={termCopy.savingsLabel}
            demandActionPast={termCopy.pastTense}
            demandPercentLabel={termCopy.savingsPercentLabel}
          />
          <LossFrameDemandBarCard
            title={`${counterpart.capitalizedTerm} ${displayedDemandLabel} ${termCopy.capitalizedTerm}`}
            actorLabel={counterpart.capitalizedTerm}
            demand={displayedPartnerAsk}
            savingsAtStakeDollars={teamWinningsBeforeAdjustment}
            demandLabel={termCopy.savingsLabel}
            demandActionPast={termCopy.pastTense}
            demandPercentLabel={termCopy.savingsPercentLabel}
          />
        </div>
      ) : null}

      {!displayedDemandsFit ? (
        <div className="space-y-4">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-5 space-y-2 text-2xl text-amber-900">
            <h3 className="text-3xl font-semibold">
              {displayedDemandLabel} {termCopy.capitalizedPluralTerm} Aren&apos;t Compatible
            </h3>
            {lossFrame ? (
              <>
                <div>
                  Together, your {termCopy.pluralTerm} totaled{" "}
                  <strong>{dollars(displayedTotalAsked)}</strong>, which is{" "}
                  <strong>{displayedTotalAskedPercent}%</strong> of the{" "}
                  <strong>{dollars(teamWinningsBeforeAdjustment)}</strong> saved amount.
                </div>
                <div>
                  That is <strong>{dollars(displayedOverDemand)}</strong> more than the saved amount.
                </div>
              </>
            ) : (
              <>
                <div>
                  We can't give you{" "}
                  <strong className="text-blue-700">{dollars(displayedMyAsk)}</strong>{" "}
                  and your {counterpart.term}{" "}
                  <strong className="text-red-700">{dollars(displayedPartnerAsk)}</strong>.
                </div>
                <div>
                  Together, your {termCopy.pluralTerm} are{" "}
                  <strong>{dollars(displayedTotalAsked)}</strong>, which is{" "}
                  <strong>{displayedTotalAskedPercent}%</strong> of the{" "}
                  <strong>{dollars(teamWinningsBeforeAdjustment)}</strong> pot.
                </div>
                <div>
                  That is <strong>{dollars(displayedOverDemand)}</strong> more than the total pot.
                </div>
              </>
            )}

          </div>
          {!lossFrame ? (
            <div className="grid lg:grid-cols-2 gap-4">
              <DemandAgainstPotChart
                title={`Your ${displayedDemandLabel} ${termCopy.capitalizedTerm}`}
                demand={displayedMyAsk}
                pot={teamWinningsBeforeAdjustment}
                color="#818cf8"
                demandLabel={termCopy.yourLabel}
                remainingLabel="Pot Left Over"
                overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
                totalLabel="pot"
                legendTextClassName="text-2xl"
                centerValueClassName="fill-blue-700 text-base font-semibold"
                centerTotalClassName="fill-gray-900 text-base font-semibold"
                chartOrderLabels={[
                  "Pot Left Over",
                  termCopy.yourLabel,
                  `${termCopy.capitalizedTerm} Above Pot`,
                ]}
              />
              <DemandAgainstPotChart
                title={`${counterpart.capitalizedTerm} ${displayedDemandLabel} ${termCopy.capitalizedTerm}`}
                demand={displayedPartnerAsk}
                pot={teamWinningsBeforeAdjustment}
                color="#f87171"
                demandLabel={termCopy.counterpartLabel}
                remainingLabel="Pot Left Over"
                overageLabel={`${termCopy.capitalizedTerm} Above Pot`}
                totalLabel="pot"
                legendTextClassName="text-2xl"
                centerValueClassName="fill-red-700 text-base font-semibold"
                centerTotalClassName="fill-gray-900 text-base font-semibold"
              />
            </div>
          ) : null}
        </div>
      ) : (
        <div className="space-y-4">
          {lossFrame && hasDisplayedSurplus ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-2xl text-amber-900">
              <h3 className="text-3xl font-semibold mb-2">Unclaimed Savings Split Evenly</h3>
              Your {termCopy.term} of{" "}
              <strong className="text-blue-700">
                {dollars(displayedMyAsk)}
              </strong>
              {`, and your ${counterpart.term}'s ${termCopy.term} of `}
              <strong className="text-red-700">
                {dollars(displayedPartnerAsk)}
              </strong>.
              Together, you asked to keep{" "}
              <strong>{dollars(displayedTotalAsked)}</strong> from the{" "}
              <strong>{dollars(teamWinningsBeforeAdjustment)}</strong> saved amount, leaving{" "}
              <u><strong>{dollars(displayedRemainingWinningsRounded)}</strong></u>{" "}
              unclaimed.
              <div className="mt-2">
                <strong>Unclaimed savings split evenly.</strong>
              </div>
            </div>
          ) : null}
          {!lossFrame && hasDisplayedSurplus ? (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-2xl text-amber-900">
              <h3 className="text-3xl font-semibold mb-2">Pot Surplus Split Evenly</h3>
              Your{" "}
              <strong className="text-blue-700">
                {dollars(displayedMyAsk)}
              </strong>{" "}
              {termCopy.term} and {counterpart.term}&apos;s{" "}
              <strong className="text-red-700">
                {dollars(displayedPartnerAsk)}
              </strong>{" "}
              {termCopy.term} from the{" "}
              <strong>{dollars(teamWinningsBeforeAdjustment)}</strong> pot left{" "}
              <u><strong>{dollars(displayedRemainingWinningsRounded)}</strong></u> unclaimed{" "}
              <strong>surplus</strong>.
              <div className="mt-2">
                <strong>Surplus split evenly.</strong>
              </div>
            </div>
          ) : null}
          {lossFrame ? (
            <LossFrameMoneyKeptBarCard
              title="Actual Game 2 Money Kept"
              subtitle={`Your final loss was ${dollars(myFinalLoss)}, so you kept ${dollars(myFinalPayoff)}.`}
              stage1={stage1}
              stage2={stage2}
              design={design}
              player={player}
              players={players}
              yourMoneyKept={myFinalPayoff}
              totalDollars={teamWinningsBeforeAdjustment}
            />
          ) : (
            <DonutChart
              title="Actual Game 2 Pay"
              subtitle={
                hasFinalSurplus
                  ? `You received your ${payoffDemandLabel} plus half of what's left over: ${dollars(myAsk)} + ${dollars(myFinalSurplus)}.`
                  : `You received your ${payoffDemandLabel}: ${dollars(myAsk)}.`
              }
              legendSide
              centerValue={myPayoff}
              centerTotal={teamWinningsBeforeAdjustment}
              centerValueClassName="fill-blue-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
              chartOrderLabels={
                hasFinalSurplus
                  ? [
                      partnerSegmentLabel,
                      `${counterpart.capitalizedTerm} surplus`,
                      "Your surplus",
                      yourSegmentLabel,
                    ]
                  : [partnerSegmentLabel, yourSegmentLabel]
              }
              segments={
                hasFinalSurplus
                  ? [
                      { label: yourSegmentLabel, value: myAsk, color: "#4f46e5" },
                      { label: "Your surplus", value: myFinalSurplus, color: "#818cf8" },
                      { label: partnerSegmentLabel, value: partnerAsk, color: "#dc2626" },
                      { label: `${counterpart.capitalizedTerm} surplus`, value: partnerFinalSurplus, color: "#f87171" },
                    ]
                  : [
                      { label: yourSegmentLabel, value: myAsk, color: "#4f46e5" },
                      { label: partnerSegmentLabel, value: partnerAsk, color: "#dc2626" },
                    ]
              }
            />
          )}
        </div>
      )}

      {triggeredRandomSplit ? (
        <>
        
          <div className="rounded-md border border-amber-200 bg-amber-50 p-5 space-y-2 text-2xl text-amber-900">
            <h3 className="text-3xl font-semibold">Random Split</h3>
            <div>
              {lossFrame ? (
                <>
                  Because your combined {termCopy.pluralTerm} were larger than
                  the saved amount, the saved money was{" "}
                  <b>shrunk</b> to <strong>{dollars(teamWinnings)}</strong> and <b>split randomly</b>.
                </>
              ) : (
                <>
                  Because your combined {termCopy.pluralTerm} were larger than
                  the pot, the pot was <b>shrunk</b> to{" "}
                  <strong>{dollars(teamWinnings)}</strong> and <b>split randomly</b>.
                </>
              )}
            </div>
          </div>
          {/* <div className="rounded-md border border-gray-200 p-5 space-y-2 text-xl text-gray-700">
            <h3 className="text-3xl font-semibold text-gray-900">Pot After No Agreement</h3>
            <div>
              The pot shrank from <strong>{dollars(teamWinningsBeforeAdjustment)}</strong> to{" "}
              <strong>{dollars(teamWinnings)}</strong> before the random split.
            </div>
          </div> */}
          {lossFrame ? (
            <LossFrameMoneyKeptBarCard
              title="Money Kept"
              subtitle={
                <>
                  <strong>{dollars(shrinkLoss)}</strong> was <strong>lost</strong> when
                  the saved money shrank. The rest was <strong>split randomly</strong>.
                </>
              }
              stage1={stage1}
              stage2={stage2}
              design={design}
              player={player}
              players={players}
              yourMoneyKept={myFinalPayoff}
              totalDollars={teamWinningsBeforeAdjustment}
              lostToShrinkDollars={shrinkLoss}
            />
          ) : (
            <DonutChart
              title="Pay Received"
              subtitle={
                <>
                  <strong>{dollars(shrinkLoss)}</strong> was <strong>lost</strong> when
                  the pot shrank. The rest was <strong>split randomly</strong>.
                </>
              }
              legendSide
              centerValue={myFinalPayoff}
              centerTotal={teamWinnings}
              centerValueClassName="fill-blue-700 text-base font-semibold"
              centerTotalClassName="fill-gray-900 text-base font-semibold"
              chartOrderLabels={[
                `${counterpart.capitalizedTerm} Game 2 Pay`,
                "Lost to Shrink",
                "Your Game 2 Pay",
              ]}
              segments={[
                { label: "Your Game 2 Pay", value: myFinalPayoff, color: "#4f46e5" },
                { label: `${counterpart.capitalizedTerm} Game 2 Pay`, value: partnerFinalPayoff, color: "#dc2626" },
                { label: "Lost to Shrink", value: shrinkLoss, color: "#6b7280" },
              ]}
            />
          )}
        </>
      ) : null}

      <div className="rounded-md border border-empirica-200 bg-empirica-50 p-5 text-4xl font-semibold text-empirica-900">
        Your Game 2 {lossFrame ? "Money Kept" : "Pay"}: <u>{dollars(myPayoff)}</u>
      </div>

      <div className="flex items-center space-x-4">
        <Button handleClick={handleContinue} autoFocus className="text-xl">
          Continue
        </Button>
      </div>
      <TestContinueWithoutInteraction onContinue={handleContinue} />
    </div>
  );
}
